import { useEffect, useMemo, useState } from 'react';
import {
  Box, Typography, Paper, Chip, Select, MenuItem, TextField, Button, Dialog,
  DialogTitle, DialogContent, DialogActions, FormControl, InputLabel,
  IconButton, Alert,
  CircularProgress, InputAdornment, Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  useDroppable,
  useSensor,
  useSensors,
  type DragCancelEvent,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import { SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { api } from '../../api/client.js';
import RatingChip from '../../components/RatingChip.js';
import type { ICheck, ICategory, IMappingRule, IPlaywrightInput, ILabel } from '../../../shared/types.js';
import { Weight, Rating } from '../../../shared/types.js';
import { colors } from '../../styles/theme.js';

function playwrightLabel(pi: IPlaywrightInput & { check_playwright_input?: { data_path: string | null } }): string {
  const path = pi.CheckPlaywrightInput?.data_path ?? pi.check_playwright_input?.data_path;
  return path ? `${pi.name} · ${path}` : pi.name;
}

function CheckCard({
  check,
  draggableId,
  columnValue,
  isSelected,
  onClick,
}: {
  check: ICheck;
  draggableId: string;
  columnValue: string;
  isSelected: boolean;
  onClick: (id: string) => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: draggableId,
    data: { columnValue },
  });
  return (
    <Paper
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      onClick={() => onClick(check.id)}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onClick(check.id);
        }
      }}
      role="button"
      tabIndex={0}
      aria-label={`Check ${check.check_code} öffnen`}
      aria-pressed={isSelected}
      sx={{
        p: '12px 14px',
        mb: 1,
        borderRadius: '12px',
        cursor: 'grab',
        border: isSelected ? '2px solid #C8E600' : '2px solid transparent',
        bgcolor: isSelected ? '#fafff0' : '#fff',
        boxShadow: isSelected ? '0 0 0 3px rgba(200,230,0,0.2)' : '0 1px 2px rgba(0,0,0,0.04)',
        opacity: isDragging ? 0.35 : 1,
        transform: CSS.Transform.toString(transform),
        transition: `all 200ms ease, transform ${transition ?? '200ms ease'}`,
        '&:hover': {
          borderColor: '#C8E600',
          boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
          transform: `${CSS.Transform.toString(transform) ?? 'translate3d(0,0,0)'} translateY(-2px)`,
        },
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5, minWidth: 0 }}>
        <Box />
        <Chip
          size="small"
          label={check.weight}
          color={check.weight === Weight.HIGH ? 'error' : check.weight === Weight.MEDIUM ? 'warning' : 'success'}
          sx={{ flexShrink: 0, minWidth: 68, fontWeight: 700, fontSize: 10, height: 20 }}
        />
      </Box>
      <Typography
        sx={{ fontSize: 14, fontWeight: 600, color: '#1E1E1E', mb: 0.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
        title={check.title}
      >
        {check.title}
      </Typography>
      <Typography
        sx={{ fontSize: 12, color: '#999', mb: 0.75, display: '-webkit-box', WebkitLineClamp: 1, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
        title={check.description ?? 'Keine Beschreibung'}
      >
        {check.description ?? 'Keine Beschreibung'}
      </Typography>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', minWidth: 0 }}>
          {(check.labels ?? []).slice(0, 2).map((label) => (
            <Chip key={label.id} label={label.name} size="small" sx={{ height: 20, fontSize: 10, maxWidth: 84, '& .MuiChip-label': { px: 0.75, overflow: 'hidden', textOverflow: 'ellipsis' } }} />
          ))}
        </Box>
        <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
          {Array.from(new Set((check.mapping_rules ?? []).map((rule) => rule.rating))).slice(0, 3).map((rating) => (
            <Box
              key={rating}
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                bgcolor: rating === Rating.G ? colors.scoreGreen : rating === Rating.Y ? colors.scoreYellow : colors.scoreRed,
              }}
            />
          ))}
        </Box>
      </Box>
    </Paper>
  );
}

function DragPreviewCard({ check }: { check: ICheck }) {
  return (
    <Paper
      sx={{
        p: '12px 14px',
        width: 290,
        borderRadius: '12px',
        cursor: 'grabbing',
        border: '2px solid #C8E600',
        bgcolor: '#fff',
        boxShadow: '0 10px 24px rgba(0,0,0,0.2)',
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5, minWidth: 0 }}>
        <Typography sx={{ fontFamily: 'monospace', fontSize: 11, color: '#888', pr: 1 }}>
          {check.check_code}
        </Typography>
        <Chip
          size="small"
          label={check.weight}
          color={check.weight === Weight.HIGH ? 'error' : check.weight === Weight.MEDIUM ? 'warning' : 'success'}
          sx={{ flexShrink: 0, minWidth: 68, fontWeight: 700, fontSize: 10, height: 20 }}
        />
      </Box>
      <Typography sx={{ fontSize: 14, fontWeight: 600, color: '#1E1E1E', mb: 0.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {check.title}
      </Typography>
      <Typography sx={{ fontSize: 12, color: '#999', mb: 0.75, display: '-webkit-box', WebkitLineClamp: 1, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
        {check.description ?? 'Keine Beschreibung'}
      </Typography>
    </Paper>
  );
}

function CategoryColumn({
  columnId,
  title,
  checks,
  selectedCheckId,
  onSelectCheck,
}: {
  columnId: string;
  title: string;
  checks: ICheck[];
  selectedCheckId: string | null;
  onSelectCheck: (id: string) => void;
}) {
  const { setNodeRef, isOver } = useDroppable({ id: columnId, data: { weight: columnId } });
  const isEmpty = checks.length === 0;
  return (
    <Paper
      ref={setNodeRef}
      role="region"
      aria-label={`Gewichtung ${title}`}
      sx={{
        minWidth: isEmpty ? 240 : 290,
        maxWidth: isEmpty ? 260 : 340,
        p: 1.5,
        bgcolor: isOver ? '#fafff0' : '#fafafa',
        borderRadius: 3,
        border: '1px solid #ececec',
      }}
    >
      <Box sx={{ mb: 1.25, px: 0.75, py: 0.75, borderRadius: 2, bgcolor: '#f2f2f2', borderLeft: '4px solid #C8E600' }}>
        <Typography sx={{ fontWeight: 600, fontSize: 13, color: '#1E1E1E' }}>{title}</Typography>
        <Typography sx={{ fontSize: 11, color: '#888' }}>{checks.length} Checks</Typography>
      </Box>
      <SortableContext items={checks.map((check) => `${columnId}::${check.id}`)} strategy={verticalListSortingStrategy}>
        {checks.map((check) => (
          <CheckCard
            key={`${columnId}-${check.id}`}
            check={check}
            draggableId={`${columnId}::${check.id}`}
            columnValue={columnId}
            isSelected={selectedCheckId === check.id}
            onClick={onSelectCheck}
          />
        ))}
      </SortableContext>
      <Box sx={{ mt: 1, p: 1, border: '1px dashed #d1d5db', borderRadius: 2, textAlign: 'center', color: 'text.secondary', fontSize: 12, minHeight: isEmpty ? 84 : 'auto', display: 'grid', placeItems: 'center' }}>
        Check hierher ziehen
      </Box>
    </Paper>
  );
}

export default function ChecksPage() {
  const [checks, setChecks] = useState<ICheck[]>([]);
  const [categories, setCategories] = useState<ICategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [activeWeightFilter, setActiveWeightFilter] = useState<'ALL' | Weight>('ALL');
  const [activeLabelFilter, setActiveLabelFilter] = useState<'ALL' | string>('ALL');
  const [selectedCheckId, setSelectedCheckId] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [newCheck, setNewCheck] = useState({ check_code: '', title: '', description: '', category_id: '', weight: Weight.MEDIUM });
  const [checkCodePrefixSelection, setCheckCodePrefixSelection] = useState<string>('CC');
  const [customCheckCodePrefix, setCustomCheckCodePrefix] = useState('');
  const [ruleDialog, setRuleDialog] = useState<{ checkId: string } | null>(null);
  const [newRule, setNewRule] = useState({ output_condition: '', rating: Rating.G, score_factor: 1.0, sort_order: 0 });
  const [allLabels, setAllLabels] = useState<ILabel[]>([]);
  const [newLabelName, setNewLabelName] = useState('');
  const [editLabel, setEditLabel] = useState<ILabel | null>(null);
  const [editLabelName, setEditLabelName] = useState('');
  const [deleteLabelTarget, setDeleteLabelTarget] = useState<ILabel | null>(null);
  const [editRule, setEditRule] = useState<IMappingRule | null>(null);
  const [editRuleForm, setEditRuleForm] = useState({ output_condition: '', rating: Rating.G, score_factor: 1.0, sort_order: 0 });
  const [deleteRuleTarget, setDeleteRuleTarget] = useState<IMappingRule | null>(null);
  const [allPlaywrightInputs, setAllPlaywrightInputs] = useState<IPlaywrightInput[]>([]);
  const [piSaveConfirm, setPiSaveConfirm] = useState<{ check: ICheck; piId: number; inputId: string } | null>(null);
  const [piDeleteConfirm, setPiDeleteConfirm] = useState<{ check: ICheck; piId: number; piName: string } | null>(null);
  const [columnMode, setColumnMode] = useState<'weight' | 'label'>('weight');
  const [activeDragCheckId, setActiveDragCheckId] = useState<string | null>(null);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const loadData = async (silent = false) => {
    if (!silent) setLoading(true);
    setError(null);
    const [checksRes, catsRes, labelsRes, piRes] = await Promise.all([
      api.get<{ checks: ICheck[] }>('/config/checks'),
      api.get<{ categories: ICategory[] }>('/config/categories'),
      api.get<{ labels: ILabel[] }>('/config/labels'),
      api.get<{ playwright_inputs: IPlaywrightInput[] }>('/config/playwright-inputs'),
    ]);
    if (checksRes.success) setChecks(checksRes.data.checks);
    if (catsRes.success) setCategories(catsRes.data.categories);
    if (labelsRes.success) setAllLabels(labelsRes.data.labels);
    if (piRes.success) setAllPlaywrightInputs(piRes.data.playwright_inputs);
    if (!checksRes.success) setError(checksRes.error.message);
    else if (!catsRes.success) setError(catsRes.error.message);
    setLoading(false);
  };

  useEffect(() => { loadData(); }, []);

  const filteredChecks = useMemo(() => {
    const q = search.trim().toLowerCase();
    const searched = checks.filter(
      (c) =>
        c.check_code.toLowerCase().includes(q)
        || c.title.toLowerCase().includes(q)
        || (c.description?.toLowerCase().includes(q) ?? false)
        || (c.category?.display_name?.toLowerCase().includes(q) ?? false),
    );
    // Inverted filtering mode:
    // - Columns by weight => filter by label
    // - Columns by label  => filter by weight
    if (columnMode === 'weight') {
      if (activeLabelFilter === 'ALL') return searched;
      if (activeLabelFilter === 'no_label') return searched.filter((check) => (check.labels?.length ?? 0) === 0);
      return searched.filter((check) => (check.labels ?? []).some((label) => String(label.id) === activeLabelFilter));
    }
    if (activeWeightFilter === 'ALL') return searched;
    return searched.filter((check) => check.weight === activeWeightFilter);
  }, [activeLabelFilter, activeWeightFilter, checks, columnMode, search]);

  const handleWeightChange = async (check: ICheck, weight: Weight) => {
    setChecks((prev) => prev.map((c) => c.id === check.id ? { ...c, weight } : c));
    const res = await api.patch(`/config/checks/${check.id}`, { weight });
    if (!res.success) loadData(true);
  };

  const handleCreateCheck = async () => {
    if (!generatedCheckCode) return;
    const payload = { ...newCheck, check_code: generatedCheckCode };
    const res = await api.post('/config/checks', payload);
    if (res.success) {
      setCreateOpen(false);
      setNewCheck({ check_code: '', title: '', description: '', category_id: '', weight: Weight.MEDIUM });
      setCheckCodePrefixSelection(existingCheckCodePrefixes[0] ?? 'CC');
      setCustomCheckCodePrefix('');
      loadData(true);
    } else {
      setError((res as { success: false; error: { message: string } }).error.message);
    }
  };

  const handleToggleLabel = async (check: ICheck, labelId: number) => {
    const current = (check.labels ?? []).map((l) => l.id);
    const next = current.includes(labelId) ? current.filter((id) => id !== labelId) : [...current, labelId];
    await api.put(`/config/checks/${check.id}/labels`, { label_ids: next });
    loadData(true);
  };

  const handleCreateLabel = async () => {
    if (!newLabelName.trim()) return;
    const res = await api.post<{ label: ILabel }>('/config/labels', { name: newLabelName.trim() });
    if (res.success) {
      setAllLabels((prev) => [...prev, res.data.label].sort((a, b) => a.name.localeCompare(b.name)));
      setNewLabelName('');
    }
  };

  const handleSaveLabel = async () => {
    if (!editLabel || !editLabelName.trim()) return;
    const res = await api.patch<{ label: ILabel }>(`/config/labels/${editLabel.id}`, { name: editLabelName.trim() });
    if (res.success) {
      setEditLabel(null);
      loadData(true);
    }
  };

  const handleDeleteLabel = async () => {
    if (!deleteLabelTarget) return;
    const res = await api.delete(`/config/labels/${deleteLabelTarget.id}`);
    if (res.success) {
      setAllLabels((prev) => prev.filter((l) => l.id !== deleteLabelTarget.id));
      setDeleteLabelTarget(null);
      loadData(true);
    }
  };

  const handleSaveRule = async () => {
    if (!editRule) return;
    const res = await api.patch(`/config/mapping-rules/${editRule.id}`, editRuleForm);
    if (res.success) { setEditRule(null); loadData(true); }
  };

  const handleConfirmDeleteRule = async () => {
    if (!deleteRuleTarget) return;
    const res = await api.delete(`/config/mapping-rules/${deleteRuleTarget.id}`);
    if (res.success) { setDeleteRuleTarget(null); loadData(true); }
  };

  const handleUpdatePlaywrightInputs = async (check: ICheck, inputIds: { id: number; data_path?: string }[]) => {
    await api.patch(`/config/checks/${check.id}`, { playwright_input_ids: inputIds });
    loadData(true);
  };

  const handleCreateRule = async () => {
    if (!ruleDialog) return;
    const res = await api.post('/config/mapping-rules', {
      check_id: ruleDialog.checkId,
      ...newRule,
    });
    if (res.success) {
      setRuleDialog(null);
      setNewRule({ output_condition: '', rating: Rating.G, score_factor: 1.0, sort_order: 0 });
      loadData(true);
    }
  };

  const selectedCheck = checks.find((check) => check.id === selectedCheckId) ?? null;
  const selectedCategory = categories.find((cat) => cat.id === selectedCheck?.category_id);
  const activeDragCheck = checks.find((check) => check.id === activeDragCheckId) ?? null;

  const checksByWeight = useMemo(() => {
    const grouped: Record<Weight, ICheck[]> = {
      [Weight.HIGH]: [],
      [Weight.MEDIUM]: [],
      [Weight.LOW]: [],
    };
    filteredChecks.forEach((check) => {
      grouped[check.weight].push(check);
    });
    (Object.keys(grouped) as Weight[]).forEach((weightKey) => {
      grouped[weightKey].sort((a, b) => {
        return a.check_code.localeCompare(b.check_code);
      });
    });
    return grouped;
  }, [filteredChecks]);

  const checksByLabel = useMemo(() => {
    const grouped: Record<string, ICheck[]> = { no_label: [] };
    allLabels.forEach((label) => { grouped[String(label.id)] = []; });
    filteredChecks.forEach((check) => {
      const labels = check.labels ?? [];
      if (labels.length === 0) {
        grouped.no_label.push(check);
        return;
      }
      labels.forEach((label) => {
        const key = String(label.id);
        if (!grouped[key]) grouped[key] = [];
        grouped[key].push(check);
      });
    });
    Object.keys(grouped).forEach((key) => {
      grouped[key].sort((a, b) => a.check_code.localeCompare(b.check_code));
    });
    return grouped;
  }, [allLabels, filteredChecks]);

  const existingCheckCodePrefixes = useMemo(() => {
    const prefixes = new Set<string>();
    checks.forEach((check) => {
      const match = /^([A-Za-z]+)_?(\d+)$/.exec(check.check_code.trim());
      if (match) prefixes.add(match[1].toUpperCase());
    });
    return Array.from(prefixes).sort((a, b) => a.localeCompare(b));
  }, [checks]);

  const effectiveCheckCodePrefix = useMemo(() => {
    if (checkCodePrefixSelection === '__custom__') {
      return customCheckCodePrefix.trim().toUpperCase().replace(/[^A-Z]/g, '');
    }
    return checkCodePrefixSelection.trim().toUpperCase();
  }, [checkCodePrefixSelection, customCheckCodePrefix]);

  const nextCheckCodeNumber = useMemo(() => {
    if (!effectiveCheckCodePrefix) return 1;
    let maxNumber = 0;
    checks.forEach((check) => {
      const match = /^([A-Za-z]+)_?(\d+)$/.exec(check.check_code.trim());
      if (!match) return;
      if (match[1].toUpperCase() !== effectiveCheckCodePrefix) return;
      const parsed = Number.parseInt(match[2], 10);
      if (Number.isFinite(parsed) && parsed > maxNumber) maxNumber = parsed;
    });
    return maxNumber + 1;
  }, [checks, effectiveCheckCodePrefix]);

  const generatedCheckCode = useMemo(() => {
    if (!effectiveCheckCodePrefix) return '';
    return `${effectiveCheckCodePrefix}_${String(nextCheckCodeNumber).padStart(3, '0')}`;
  }, [effectiveCheckCodePrefix, nextCheckCodeNumber]);

  useEffect(() => {
    if (!createOpen) return;
    if (checkCodePrefixSelection !== '__custom__' && existingCheckCodePrefixes.length > 0) {
      const hasCurrent = existingCheckCodePrefixes.includes(checkCodePrefixSelection);
      if (!hasCurrent) setCheckCodePrefixSelection(existingCheckCodePrefixes[0]);
    }
    setNewCheck((prev) => ({ ...prev, check_code: generatedCheckCode }));
  }, [checkCodePrefixSelection, createOpen, existingCheckCodePrefixes, generatedCheckCode]);

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) {
      setActiveDragCheckId(null);
      return;
    }
    const activeId = String(active.id);
    const checkId = activeId.includes('::') ? activeId.split('::')[1] : activeId;
    setActiveDragCheckId(null);
    const rawOverValue = String(over.data.current?.columnValue ?? over.data.current?.weight ?? over.id);
    const overColumnValue = rawOverValue.includes('::') ? rawOverValue.split('::')[0] : rawOverValue;

    if (columnMode === 'weight') {
      if (![Weight.HIGH, Weight.MEDIUM, Weight.LOW].includes(overColumnValue as Weight)) return;
      const targetWeight = overColumnValue as Weight;
      setChecks((prev) => prev.map((check) => (check.id === checkId ? { ...check, weight: targetWeight } : check)));
      const res = await api.patch(`/config/checks/${checkId}`, { weight: targetWeight });
      if (!res.success) loadData(true);
      return;
    }
    const targetLabelId = overColumnValue;
    if (targetLabelId === 'no_label') return;
    const check = checks.find((entry) => entry.id === checkId);
    if (!check) return;
    const currentIds = (check.labels ?? []).map((label) => label.id);
    const numericTarget = Number(targetLabelId);
    if (!Number.isFinite(numericTarget)) return;
    if (currentIds.includes(numericTarget)) return;
    const next = [...currentIds, numericTarget];
    const res = await api.put(`/config/checks/${checkId}/labels`, { label_ids: next });
    if (!res.success) loadData(true);
    else loadData(true);
  };

  const handleDragStart = (event: DragStartEvent) => {
    const activeId = String(event.active.id);
    const checkId = activeId.includes('::') ? activeId.split('::')[1] : activeId;
    setActiveDragCheckId(checkId);
  };

  const handleDragCancel = (_event: DragCancelEvent) => {
    setActiveDragCheckId(null);
  };

  return (
    <Box component="section" aria-label="Checks Konfiguration">
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h5">Checks</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)} aria-label="Neuen Check erstellen">
          Neuer Check
        </Button>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Alle Checks aus der Datenbank. Zeile aufklappen für Bearbeitung, Mapping Rules und Details.
      </Typography>

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 5, mb: 2.5, flexWrap: 'nowrap' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 4, minWidth: 0 }}>
          <Box sx={{ flex: '0 0 420px', minWidth: 360 }}>
            <TextField
              size="medium"
              placeholder="Suche nach Code, Titel, Beschreibung, Kategorie…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              inputProps={{ 'aria-label': 'Checks durchsuchen' }}
              fullWidth
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '14px',
              },
            }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" color="action" />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          <Paper
            elevation={0}
            sx={{
              p: 0.25,
              borderRadius: '999px',
              border: 'none',
              boxShadow: 'none',
              bgcolor: '#f3f4f6',
              height: 40,
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <Box sx={{ display: 'flex', flexWrap: 'nowrap', gap: 1 }}>
              <Chip
                label="Alle"
                size="medium"
                onClick={() => (columnMode === 'weight' ? setActiveLabelFilter('ALL') : setActiveWeightFilter('ALL'))}
                sx={{
                  minWidth: 84,
                  height: 32,
                  justifyContent: 'center',
                  fontWeight: 700,
                  fontSize: 12,
                  bgcolor: (columnMode === 'weight' ? activeLabelFilter === 'ALL' : activeWeightFilter === 'ALL') ? colors.black : '#f3f4f6',
                  color: (columnMode === 'weight' ? activeLabelFilter === 'ALL' : activeWeightFilter === 'ALL') ? colors.lime : '#374151',
                }}
              />

              {columnMode === 'label' && [Weight.HIGH, Weight.MEDIUM, Weight.LOW].map((weight) => (
                <Chip
                  key={weight}
                  label={weight}
                  size="medium"
                  onClick={() => setActiveWeightFilter(weight)}
                  sx={{
                    minWidth: 84,
                    height: 32,
                    justifyContent: 'center',
                    fontWeight: 700,
                    fontSize: 12,
                    bgcolor: activeWeightFilter === weight ? colors.black : '#f3f4f6',
                    color: activeWeightFilter === weight ? colors.lime : '#374151',
                  }}
                />
              ))}

              {columnMode === 'weight' && (
                <>
                  {allLabels.slice(0, 4).map((label) => (
                    <Chip
                      key={label.id}
                      label={label.name}
                      size="medium"
                      onClick={() => setActiveLabelFilter(String(label.id))}
                      sx={{
                        minWidth: 84,
                      height: 32,
                        justifyContent: 'center',
                        fontWeight: 700,
                        fontSize: 12,
                        maxWidth: 120,
                        bgcolor: activeLabelFilter === String(label.id) ? colors.black : '#f3f4f6',
                        color: activeLabelFilter === String(label.id) ? colors.lime : '#374151',
                        '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' },
                      }}
                    />
                  ))}
                  <Chip
                    label="Ohne Label"
                    size="medium"
                    onClick={() => setActiveLabelFilter('no_label')}
                    sx={{
                      minWidth: 96,
                      height: 32,
                      justifyContent: 'center',
                      fontWeight: 700,
                      fontSize: 12,
                      bgcolor: activeLabelFilter === 'no_label' ? colors.black : '#f3f4f6',
                      color: activeLabelFilter === 'no_label' ? colors.lime : '#374151',
                    }}
                  />
                </>
              )}
            </Box>
          </Paper>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', minWidth: 220 }}>
          <Paper
            elevation={0}
            sx={{
              p: 0.25,
              borderRadius: '999px',
              border: 'none',
              boxShadow: 'none',
              bgcolor: '#f3f4f6',
              height: 40,
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
              <Button
                size="medium"
                variant={columnMode === 'weight' ? 'contained' : 'outlined'}
                onClick={() => setColumnMode('weight')}
                aria-pressed={columnMode === 'weight'}
                sx={{
                  borderRadius: '50px',
                  minWidth: 112,
                  height: 32,
                  px: 1.5,
                  fontWeight: 700,
                  fontSize: 13,
                  textTransform: 'none',
                  borderColor: 'transparent',
                  ...(columnMode === 'weight'
                    ? { bgcolor: colors.black, color: colors.lime, '&:hover': { bgcolor: '#111' } }
                    : { bgcolor: 'transparent', color: '#374151', '&:hover': { bgcolor: '#e5e7eb', borderColor: 'transparent' } }),
                }}
              >
                Gewichtung
              </Button>
              <Button
                size="medium"
                variant={columnMode === 'label' ? 'contained' : 'outlined'}
                onClick={() => setColumnMode('label')}
                aria-pressed={columnMode === 'label'}
                sx={{
                  borderRadius: '50px',
                  minWidth: 88,
                  height: 32,
                  px: 1.5,
                  fontWeight: 700,
                  fontSize: 13,
                  textTransform: 'none',
                  borderColor: 'transparent',
                  ...(columnMode === 'label'
                    ? { bgcolor: colors.black, color: colors.lime, '&:hover': { bgcolor: '#111' } }
                    : { bgcolor: 'transparent', color: '#374151', '&:hover': { bgcolor: '#e5e7eb', borderColor: 'transparent' } }),
                }}
              >
                Label
              </Button>
            </Box>
          </Paper>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)} role="alert">{error}</Alert>}
      {loading && (
        <Box sx={{ py: 8, textAlign: 'center' }} role="status" aria-live="polite" aria-label="Checks werden geladen">
          <CircularProgress size={32} />
        </Box>
      )}
      {!loading && (
        <Box role="region" aria-label="Checks Kanban Board">
          <DndContext
            sensors={sensors}
            onDragStart={handleDragStart}
            onDragCancel={handleDragCancel}
            onDragEnd={(event) => void handleDragEnd(event)}
          >
            <Box sx={{ display: 'flex', gap: 2, overflowX: 'auto', pb: 1 }}>
              {columnMode === 'weight' && [Weight.HIGH, Weight.MEDIUM, Weight.LOW].map((weight) => (
                <CategoryColumn
                  key={weight}
                  columnId={weight}
                  title={weight}
                  checks={checksByWeight[weight] ?? []}
                  selectedCheckId={selectedCheckId}
                  onSelectCheck={(id) => setSelectedCheckId(id)}
                />
              ))}
              {columnMode === 'label' && (
                <>
                  {allLabels.map((label) => (
                    <CategoryColumn
                      key={`label-${label.id}`}
                      columnId={String(label.id)}
                      title={label.name}
                      checks={checksByLabel[String(label.id)] ?? []}
                      selectedCheckId={selectedCheckId}
                      onSelectCheck={(id) => setSelectedCheckId(id)}
                    />
                  ))}
                  <CategoryColumn
                    key="label-no-label"
                    columnId="no_label"
                    title="Ohne Label"
                    checks={checksByLabel.no_label ?? []}
                    selectedCheckId={selectedCheckId}
                    onSelectCheck={(id) => setSelectedCheckId(id)}
                  />
                </>
              )}
            </Box>
            <DragOverlay>
              {activeDragCheck ? <DragPreviewCard check={activeDragCheck} /> : null}
            </DragOverlay>
          </DndContext>
        </Box>
      )}
      <Dialog
        open={Boolean(selectedCheck)}
        onClose={() => setSelectedCheckId(null)}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            border: `1px solid ${colors.silver}`,
            bgcolor: '#fbfbfb',
            width: '92vw',
            maxWidth: '1200px',
            minHeight: 'unset',
            height: 'auto',
          },
        }}
      >
        {selectedCheck && (
          <DialogContent sx={{ p: 1.75 }}>
            <Box sx={{ position: 'relative', mb: 2 }}>
              <Typography variant="h6">
                ⚙️ Checks › <Box component="span" sx={{ cursor: 'pointer' }} onClick={() => setSelectedCheckId(null)}>{selectedCategory?.display_name ?? '-'}</Box> › {selectedCheck.check_code}
              </Typography>
              <IconButton aria-label="Detailansicht schließen" onClick={() => setSelectedCheckId(null)} sx={{ position: 'absolute', top: 0, right: 0 }}>
                <CloseIcon />
              </IconButton>
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', xl: '1fr 1fr' }, gap: 2.25 }}>
              <Paper sx={{ p: 2.25, borderRadius: 3, border: `1px solid ${colors.silver}`, minHeight: 230 }}>
                <Typography variant="subtitle2" sx={{ mb: 1.5 }}>Grunddaten</Typography>
                <Box sx={{ display: 'flex', gap: 2, mb: 1.5, flexWrap: 'wrap' }}>
                  <TextField
                    label="Titel"
                    defaultValue={selectedCheck.title}
                    size="small"
                    sx={{ flex: '1 1 220px', minWidth: 220 }}
                    onBlur={(e) => {
                      if (e.target.value !== selectedCheck.title) {
                        api.patch(`/config/checks/${selectedCheck.id}`, { title: e.target.value }).then(() => loadData(true));
                      }
                    }}
                  />
                  <FormControl size="small" sx={{ minWidth: 160 }}>
                    <InputLabel>Gewichtung</InputLabel>
                    <Select value={selectedCheck.weight} label="Gewichtung" onChange={(e) => handleWeightChange(selectedCheck, e.target.value as Weight)}>
                      <MenuItem value={Weight.LOW}>LOW (1)</MenuItem>
                      <MenuItem value={Weight.MEDIUM}>MEDIUM (5)</MenuItem>
                      <MenuItem value={Weight.HIGH}>HIGH (10)</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
                <TextField
                  label="Beschreibung"
                  defaultValue={selectedCheck.description ?? ''}
                  size="small"
                  fullWidth
                  multiline
                  rows={3}
                  onBlur={(e) => api.patch(`/config/checks/${selectedCheck.id}`, { description: e.target.value }).then(() => loadData(true))}
                />
                <TextField
                  label="Info-Box"
                  defaultValue={selectedCheck.info_box ?? ''}
                  size="small"
                  fullWidth
                  multiline
                  rows={2}
                  sx={{ mt: 1.5 }}
                  onBlur={(e) => api.patch(`/config/checks/${selectedCheck.id}`, { info_box: e.target.value || null }).then(() => loadData(true))}
                />
              </Paper>

              <Paper sx={{ p: 2.25, borderRadius: 3, border: `1px solid ${colors.silver}`, minHeight: 230 }}>
                <Typography variant="subtitle2" sx={{ mb: 1.5 }}>Labels</Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1.25 }}>
                  {allLabels.map((label) => {
                    const isAssigned = (selectedCheck.labels ?? []).some((l) => l.id === label.id);
                    return (
                      <Box key={label.id} sx={{ display: 'inline-flex', alignItems: 'center', gap: 0 }}>
                        <Chip
                          label={label.name}
                          size="small"
                          onClick={() => handleToggleLabel(selectedCheck, label.id)}
                          sx={{
                            cursor: 'pointer',
                            bgcolor: isAssigned ? (label.color ?? colors.black) : 'transparent',
                            color: isAssigned ? '#fff' : colors.black60,
                            border: `1px solid ${isAssigned ? (label.color ?? colors.black) : colors.silver}`,
                            borderRadius: '16px 0 0 16px',
                            '&:hover': { opacity: 0.8 },
                          }}
                        />
                        <Box sx={{ display: 'flex', alignItems: 'center', border: `1px solid ${colors.silver}`, borderLeft: 'none', borderRadius: '0 16px 16px 0', px: 0.25, height: 24, bgcolor: 'transparent' }}>
                          <IconButton size="small" aria-label={`Label ${label.name} bearbeiten`} onClick={() => { setEditLabel(label); setEditLabelName(label.name); }} sx={{ p: 0.25 }}>
                            <EditIcon sx={{ fontSize: 13, color: colors.black60 }} />
                          </IconButton>
                          <IconButton size="small" aria-label={`Label ${label.name} löschen`} onClick={() => setDeleteLabelTarget(label)} sx={{ p: 0.25 }}>
                            <DeleteOutlineIcon sx={{ fontSize: 13, color: colors.black60 }} />
                          </IconButton>
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <TextField
                    size="small"
                    placeholder="Neues Label…"
                    value={newLabelName}
                    onChange={(e) => setNewLabelName(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleCreateLabel(); }}
                    sx={{ width: 220 }}
                  />
                  <IconButton size="small" aria-label="Neues Label erstellen" onClick={handleCreateLabel} disabled={!newLabelName.trim()}>
                    <AddIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Paper>

              <Paper sx={{ p: 2.25, borderRadius: 3, border: `1px solid ${colors.silver}`, minHeight: 250 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.25 }}>
                  <Typography variant="subtitle2">Mapping Rules</Typography>
                  <Button size="small" startIcon={<AddIcon />} onClick={() => setRuleDialog({ checkId: selectedCheck.id })}>
                    Regel hinzufügen
                  </Button>
                </Box>
                <Box sx={{ display: 'grid', gap: 0.75 }}>
                  {(selectedCheck.mapping_rules ?? []).map((rule) => (
                    <Box key={rule.id} sx={{ display: 'grid', gridTemplateColumns: '20px 1fr auto auto auto', alignItems: 'center', gap: 1, border: `1px solid ${colors.silver}`, borderRadius: 2, p: 0.75 }}>
                      <DragIndicatorIcon sx={{ fontSize: 16, color: colors.black60 }} />
                      <Typography variant="caption" sx={{ wordBreak: 'break-word' }}>{rule.output_condition}</Typography>
                      <RatingChip rating={rule.rating} />
                      <Typography variant="caption">{rule.score_factor}</Typography>
                      <Box>
                        <IconButton size="small" aria-label="Mapping Rule bearbeiten" onClick={() => { setEditRule(rule); setEditRuleForm({ output_condition: rule.output_condition, rating: rule.rating, score_factor: rule.score_factor, sort_order: rule.sort_order }); }}>
                          <EditIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                        <IconButton size="small" aria-label="Mapping Rule löschen" onClick={() => setDeleteRuleTarget(rule)}>
                          <DeleteOutlineIcon sx={{ fontSize: 16, color: colors.scoreRed }} />
                        </IconButton>
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Paper>

              <Paper sx={{ p: 2.25, borderRadius: 3, border: `1px solid ${colors.silver}`, minHeight: 250 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Playwright-Inputs</Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                  {allPlaywrightInputs.map((pi) => {
                    const isAssigned = (selectedCheck.playwright_inputs ?? []).some((a) => a.id === pi.id);
                    return (
                      <Chip
                        key={pi.id}
                        label={pi.name}
                        size="small"
                        onClick={() => {
                          if (isAssigned) return;
                          const current = (selectedCheck.playwright_inputs ?? []).map((a) => ({ id: a.id, data_path: a.CheckPlaywrightInput?.data_path ?? undefined }));
                          handleUpdatePlaywrightInputs(selectedCheck, [...current, { id: pi.id }]);
                        }}
                        sx={{
                          cursor: isAssigned ? 'default' : 'pointer',
                          bgcolor: isAssigned ? colors.black : 'transparent',
                          color: isAssigned ? '#fff' : colors.black60,
                          border: `1px solid ${isAssigned ? colors.black : colors.silver}`,
                          '&:hover': { opacity: isAssigned ? 1 : 0.8 },
                        }}
                      />
                    );
                  })}
                </Box>
                {(selectedCheck.playwright_inputs ?? []).map((pi) => {
                  const dp = pi.CheckPlaywrightInput?.data_path ?? '';
                  const inputId = `pi-${selectedCheck.id}-${pi.id}`;
                  return (
                    <Box key={pi.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                      <Typography variant="caption" sx={{ minWidth: 140, fontWeight: 600 }}>{pi.name}:</Typography>
                      <TextField id={inputId} size="small" placeholder="data_path (z.B. dataLayer.consent)" defaultValue={dp} sx={{ flex: 1, '& input': { fontSize: '0.8rem' } }} />
                      <Tooltip title="Speichern">
                        <IconButton size="small" aria-label={`Playwright-Input ${pi.name} speichern`} onClick={() => setPiSaveConfirm({ check: selectedCheck, piId: pi.id, inputId })} sx={{ color: colors.scoreGreen }}>
                          <SaveIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Entfernen">
                        <IconButton size="small" aria-label={`Playwright-Input ${pi.name} entfernen`} onClick={() => setPiDeleteConfirm({ check: selectedCheck, piId: pi.id, piName: pi.name })}>
                          <DeleteOutlineIcon sx={{ fontSize: 18, color: colors.scoreRed }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  );
                })}
              </Paper>
            </Box>
          </DialogContent>
        )}
      </Dialog>

      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Neuer Check</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '16px !important' }}>
          <FormControl>
            <InputLabel>Check-Code Prefix</InputLabel>
            <Select
              value={checkCodePrefixSelection}
              label="Check-Code Prefix"
              onChange={(e) => setCheckCodePrefixSelection(String(e.target.value))}
            >
              {existingCheckCodePrefixes.map((prefix) => (
                <MenuItem key={prefix} value={prefix}>{prefix}</MenuItem>
              ))}
              <MenuItem value="__custom__">Neu eingeben…</MenuItem>
            </Select>
          </FormControl>
          {checkCodePrefixSelection === '__custom__' && (
            <TextField
              label="Neuer Prefix"
              placeholder="z.B. CC"
              value={customCheckCodePrefix}
              onChange={(e) => setCustomCheckCodePrefix(e.target.value)}
              helperText="Nur Buchstaben; die Nummer wird automatisch vergeben."
            />
          )}
          <TextField
            label="Check Code"
            value={generatedCheckCode}
            InputProps={{ readOnly: true }}
            helperText="Automatisch generiert: letzte vergebene Nummer + 1"
          />
          <TextField label="Titel" value={newCheck.title}
            onChange={(e) => setNewCheck({ ...newCheck, title: e.target.value })} />
          <TextField label="Beschreibung" multiline rows={2} value={newCheck.description}
            onChange={(e) => setNewCheck({ ...newCheck, description: e.target.value })} />
          <FormControl>
            <InputLabel>Kategorie</InputLabel>
            <Select value={newCheck.category_id || ''} label="Kategorie"
              onChange={(e) => setNewCheck({ ...newCheck, category_id: String(e.target.value) })}>
              {categories.map((cat) => <MenuItem key={cat.id} value={cat.id}>{cat.display_name}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl>
            <InputLabel>Gewichtung</InputLabel>
            <Select value={newCheck.weight} label="Gewichtung"
              onChange={(e) => setNewCheck({ ...newCheck, weight: e.target.value as Weight })}>
              <MenuItem value={Weight.LOW}>LOW (1)</MenuItem>
              <MenuItem value={Weight.MEDIUM}>MEDIUM (5)</MenuItem>
              <MenuItem value={Weight.HIGH}>HIGH (10)</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Abbrechen</Button>
          <Button
            variant="contained"
            onClick={handleCreateCheck}
            disabled={!generatedCheckCode || !newCheck.title.trim() || !newCheck.category_id}
          >
            Erstellen
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!ruleDialog} onClose={() => setRuleDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Neue Mapping Rule</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '16px !important' }}>
          <TextField label="Bedingung (output_condition)" multiline rows={2} value={newRule.output_condition}
            onChange={(e) => setNewRule({ ...newRule, output_condition: e.target.value })} />
          <FormControl>
            <InputLabel>Rating</InputLabel>
            <Select value={newRule.rating} label="Rating"
              onChange={(e) => {
                const r = e.target.value as Rating;
                setNewRule({ ...newRule, rating: r, score_factor: r === Rating.G ? 1.0 : r === Rating.Y ? 0.5 : 0.0 });
              }}>
              <MenuItem value={Rating.G}>G (Grün — 1.0)</MenuItem>
              <MenuItem value={Rating.Y}>Y (Gelb — 0.5)</MenuItem>
              <MenuItem value={Rating.R}>R (Rot — 0.0)</MenuItem>
            </Select>
          </FormControl>
          <TextField label="Sort Order" type="number" value={newRule.sort_order}
            onChange={(e) => setNewRule({ ...newRule, sort_order: parseInt(e.target.value, 10) || 0 })} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRuleDialog(null)}>Abbrechen</Button>
          <Button variant="contained" onClick={handleCreateRule}>Erstellen</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!editLabel} onClose={() => setEditLabel(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Label bearbeiten</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '16px !important' }}>
          <TextField
            label="Name"
            value={editLabelName}
            onChange={(e) => setEditLabelName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleSaveLabel(); }}
            size="small"
            autoFocus
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditLabel(null)}>Abbrechen</Button>
          <Button variant="contained" onClick={handleSaveLabel} disabled={!editLabelName.trim()}>Speichern</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!deleteLabelTarget} onClose={() => setDeleteLabelTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Label löschen?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Möchtest du das Label <strong>{deleteLabelTarget?.name}</strong> wirklich löschen? Es wird von allen Checks entfernt.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteLabelTarget(null)}>Abbrechen</Button>
          <Button variant="contained" color="error" onClick={handleDeleteLabel}>Löschen</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!editRule} onClose={() => setEditRule(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Mapping Rule bearbeiten</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '16px !important' }}>
          <TextField
            label="Bedingung (output_condition)"
            multiline rows={2}
            value={editRuleForm.output_condition}
            onChange={(e) => setEditRuleForm({ ...editRuleForm, output_condition: e.target.value })}
          />
          <FormControl>
            <InputLabel>Rating</InputLabel>
            <Select value={editRuleForm.rating} label="Rating"
              onChange={(e) => {
                const r = e.target.value as Rating;
                setEditRuleForm({ ...editRuleForm, rating: r, score_factor: r === Rating.G ? 1.0 : r === Rating.Y ? 0.5 : 0.0 });
              }}>
              <MenuItem value={Rating.G}>G (Grün — 1.0)</MenuItem>
              <MenuItem value={Rating.Y}>Y (Gelb — 0.5)</MenuItem>
              <MenuItem value={Rating.R}>R (Rot — 0.0)</MenuItem>
            </Select>
          </FormControl>
          <TextField label="Sort Order" type="number" value={editRuleForm.sort_order}
            onChange={(e) => setEditRuleForm({ ...editRuleForm, sort_order: parseInt(e.target.value, 10) || 0 })} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditRule(null)}>Abbrechen</Button>
          <Button variant="contained" onClick={handleSaveRule}>Speichern</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!deleteRuleTarget} onClose={() => setDeleteRuleTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Mapping Rule löschen?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Möchtest du diese Regel wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            Bedingung: {deleteRuleTarget?.output_condition}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteRuleTarget(null)}>Abbrechen</Button>
          <Button variant="contained" color="error" onClick={handleConfirmDeleteRule}>Löschen</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!piSaveConfirm} onClose={() => setPiSaveConfirm(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Playwright-Input speichern?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Möchtest du die Änderung am Playwright-Input wirklich speichern?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPiSaveConfirm(null)}>Abbrechen</Button>
          <Button variant="contained" onClick={() => {
            if (!piSaveConfirm) return;
            const { check, piId, inputId } = piSaveConfirm;
            const el = document.getElementById(inputId) as HTMLInputElement | null;
            if (!el) { setPiSaveConfirm(null); return; }
            const current = (check.playwright_inputs ?? []).map((a) => ({
              id: a.id,
              data_path: a.id === piId ? (el.value.trim() || undefined) : ((a as any).CheckPlaywrightInput?.data_path ?? (a as any).check_playwright_input?.data_path ?? undefined),
            }));
            setPiSaveConfirm(null);
            handleUpdatePlaywrightInputs(check, current);
          }}>Speichern</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!piDeleteConfirm} onClose={() => setPiDeleteConfirm(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Playwright-Input entfernen?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Möchtest du den Playwright-Input <strong>{piDeleteConfirm?.piName}</strong> wirklich von diesem Check entfernen?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPiDeleteConfirm(null)}>Abbrechen</Button>
          <Button variant="contained" color="error" onClick={() => {
            if (!piDeleteConfirm) return;
            const { check, piId } = piDeleteConfirm;
            const current = (check.playwright_inputs ?? []).map((a) => ({ id: a.id, data_path: (a as any).CheckPlaywrightInput?.data_path ?? (a as any).check_playwright_input?.data_path ?? undefined }));
            setPiDeleteConfirm(null);
            handleUpdatePlaywrightInputs(check, current.filter((c) => c.id !== piId));
          }}>Entfernen</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
